/**
 * Clavier et souris.
 *
 * On lit `event.code`, qui designe la touche *physique* : KeyW/KeyA couvrent
 * donc Z/Q en AZERTY sans table de correspondance. Les lettres de `event.key`
 * sont acceptees en plus, pour les dispositions exotiques.
 *
 * La souris pointe la tourelle, et demande donc le verrouillage du pointeur :
 * sans lui, on ne peut pas tourner de plus d'un demi-ecran, et le curseur sort
 * de la fenetre. cf. doc/06-boucle-de-jeu-et-perf.md
 */

export interface InputState {
  /** -1 (marche arriere) a 1 (marche avant). */
  forward: number;
  /** -1 (droite) a 1 (gauche), rotation de la caisse. */
  turn: number;
  /** -1 a 1, rotation de la tourelle au clavier. */
  traverse: number;
  /** -1 a 1, pointage du canon au clavier. */
  elevate: number;
  /**
   * Deplacement souris accumule depuis la frame precedente, en radians.
   * Remis a zero par `endFrame`.
   */
  mouseYaw: number;
  mousePitch: number;
  /** Vrai pendant la frame ou le tir est demande. */
  fire: boolean;
  /** Vrai tant que la lunette est tenue. */
  sight: boolean;
}

const FORWARD_CODES = ['KeyW', 'ArrowUp'];
const BACK_CODES = ['KeyS', 'ArrowDown'];
const LEFT_CODES = ['KeyA', 'ArrowLeft'];
const RIGHT_CODES = ['KeyD', 'ArrowRight'];
/** Tourelle : J/L a gauche/droite, I/K pour le site. */
const TRAVERSE_LEFT = ['KeyJ'];
const TRAVERSE_RIGHT = ['KeyL'];
const ELEVATE_UP = ['KeyI'];
const ELEVATE_DOWN = ['KeyK'];
const FIRE_CODES = ['Space'];
const SIGHT_CODES = ['ShiftLeft', 'ShiftRight'];

/** Lettres, pour les dispositions que `code` ne couvre pas. */
const FORWARD_KEYS = ['z', 'w'];
const BACK_KEYS = ['s'];
const LEFT_KEYS = ['q', 'a'];
const RIGHT_KEYS = ['d'];
const TRAVERSE_LEFT_KEYS = ['j'];
const TRAVERSE_RIGHT_KEYS = ['l'];
const ELEVATE_UP_KEYS = ['i'];
const ELEVATE_DOWN_KEYS = ['k'];

/** Radians de rotation par pixel de souris. */
const MOUSE_SENSITIVITY = 0.0022;
/** Le site est plus fin que le gisement : on vise au metre a 250 m. */
const MOUSE_PITCH_RATIO = 0.42;

/**
 * Deplacement maximal retenu pour un seul evenement souris, en pixels.
 *
 * Le verrouillage du pointeur produit des pics : au moment ou le verrou est
 * pris, et au retour d'un alt-tab, le navigateur peut rapporter un movementX
 * de plusieurs milliers de pixels -- l'ecart entre l'ancienne position du
 * curseur et le centre de l'ecran. Non borne, un seul de ces evenements
 * envoie la consigne de tourelle a l'autre bout du monde.
 */
const MAX_MOUSE_STEP = 160;

/**
 * Duree pendant laquelle la souris est ignoree apres la prise du verrou, en
 * millisecondes.
 *
 * C'est a cet instant precis que le navigateur rapporte le saut entre
 * l'ancienne position du curseur et le centre de l'ecran. Jeter ces premiers
 * evenements traite la cause ; le bornage ci-dessus reste le filet pour les
 * pics d'alt-tab.
 */
const LOCK_SETTLE_MS = 180;

export class Input {
  readonly state: InputState = {
    forward: 0,
    turn: 0,
    traverse: 0,
    elevate: 0,
    mouseYaw: 0,
    mousePitch: 0,
    fire: false,
    sight: false,
  };

  /** Vrai des qu'une commande a ete utilisee : sert a lancer la partie. */
  anyPressed = false;
  /** Vrai quand le pointeur est verrouille sur le canvas. */
  pointerLocked = false;

  private readonly down = new Set<string>();
  /** Le tir est un front montant : maintenir la touche ne tire qu'un coup. */
  private firePending = false;
  private fireHeld = false;
  /** Bouton droit tenu : il partage l'etat de lunette avec la touche Maj. */
  private rightHeld = false;
  /** Date avant laquelle les evenements souris sont jetes. */
  private settleUntil = 0;

  private readonly onKeyDown = (e: KeyboardEvent) => this.setKey(e, true);
  private readonly onKeyUp = (e: KeyboardEvent) => this.setKey(e, false);
  private readonly onBlur = () => this.reset();
  private readonly onMouseMove = (e: MouseEvent) => {
    if (!this.pointerLocked) return;
    if (performance.now() < this.settleUntil) return;
    const dx = clampStep(e.movementX);
    const dy = clampStep(e.movementY);
    this.state.mouseYaw += dx * MOUSE_SENSITIVITY;
    this.state.mousePitch += dy * MOUSE_SENSITIVITY * MOUSE_PITCH_RATIO;
    this.anyPressed = true;
  };
  private readonly onMouseDown = (e: MouseEvent) => {
    if (!this.pointerLocked) return;
    if (e.button === 0) {
      if (!this.fireHeld) this.firePending = true;
      this.fireHeld = true;
    }
    // Bouton droit : la lunette, comme le clic droit d'un jeu de tir.
    if (e.button === 2) {
      this.rightHeld = true;
      this.refresh();
    }
    this.anyPressed = true;
  };
  private readonly onMouseUp = (e: MouseEvent) => {
    if (e.button === 0) this.fireHeld = false;
    if (e.button === 2) {
      this.rightHeld = false;
      this.refresh();
    }
  };
  private readonly onContextMenu = (e: MouseEvent) => {
    // Sans ca, le clic droit ouvre le menu du navigateur au lieu de viser.
    if (this.pointerLocked) e.preventDefault();
  };
  private readonly onPointerLockChange = () => {
    this.pointerLocked = document.pointerLockElement === this.canvas;
    if (this.pointerLocked) {
      this.settleUntil = performance.now() + LOCK_SETTLE_MS;
      this.state.mouseYaw = 0;
      this.state.mousePitch = 0;
    }
    if (!this.pointerLocked) {
      // Perdre le verrou (Echap) relache tout : sinon le char part en rond
      // pendant que le joueur regarde ailleurs.
      this.down.clear();
      this.fireHeld = false;
      this.rightHeld = false;
      this.refresh();
    }
  };

  constructor(private readonly canvas: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('contextmenu', this.onContextMenu);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
  }

  /**
   * Demande le verrouillage du pointeur.
   *
   * L'appel echoue si le geste de l'utilisateur n'est pas reconnu comme tel,
   * ou si le document n'a pas le focus ; il echoue aussi dans un navigateur
   * sans interface. Dans tous les cas le jeu doit rester jouable au clavier,
   * donc on avale l'erreur.
   */
  requestPointerLock(): void {
    try {
      const result = this.canvas.requestPointerLock() as unknown;
      if (result instanceof Promise) result.catch(() => undefined);
    } catch {
      /* clavier seul : tant pis pour la souris */
    }
  }

  exitPointerLock(): void {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }

  /** Vrai une seule fois par appui : a appeler une fois par frame. */
  consumeFire(): boolean {
    const fired = this.firePending;
    this.firePending = false;
    return fired;
  }

  /** Remet a zero les deltas de souris. A appeler en fin de frame. */
  endFrame(): void {
    this.state.mouseYaw = 0;
    this.state.mousePitch = 0;
    this.state.fire = false;
  }

  reset(): void {
    this.down.clear();
    this.firePending = false;
    this.fireHeld = false;
    this.rightHeld = false;
    this.state.mouseYaw = 0;
    this.state.mousePitch = 0;
    this.refresh();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('contextmenu', this.onContextMenu);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
  }

  // --- interne ---

  private setKey(e: KeyboardEvent, pressed: boolean): void {
    const tokens = [e.code, e.key.toLowerCase()];
    let touched = false;
    for (const t of tokens) {
      if (!ALL_TOKENS.has(t)) continue;
      touched = true;
      if (pressed) this.down.add(t);
      else this.down.delete(t);
    }
    if (!touched) return;

    if (pressed) {
      this.anyPressed = true;
      // Front montant du tir : `keydown` se repete quand on maintient.
      if (FIRE_CODES.includes(e.code) && !e.repeat) this.firePending = true;
    }
    // Sans ca, les fleches et la barre d'espace font defiler la page.
    if (e.code.startsWith('Arrow') || e.code === 'Space') e.preventDefault();
    this.refresh();
  }

  private any(list: string[]): boolean {
    return list.some((k) => this.down.has(k));
  }

  private refresh(): void {
    const s = this.state;
    s.forward =
      (this.any(FORWARD_CODES) || this.any(FORWARD_KEYS) ? 1 : 0) -
      (this.any(BACK_CODES) || this.any(BACK_KEYS) ? 1 : 0);
    s.turn =
      (this.any(LEFT_CODES) || this.any(LEFT_KEYS) ? 1 : 0) -
      (this.any(RIGHT_CODES) || this.any(RIGHT_KEYS) ? 1 : 0);
    s.traverse =
      (this.any(TRAVERSE_LEFT) || this.any(TRAVERSE_LEFT_KEYS) ? 1 : 0) -
      (this.any(TRAVERSE_RIGHT) || this.any(TRAVERSE_RIGHT_KEYS) ? 1 : 0);
    s.elevate =
      (this.any(ELEVATE_UP) || this.any(ELEVATE_UP_KEYS) ? 1 : 0) -
      (this.any(ELEVATE_DOWN) || this.any(ELEVATE_DOWN_KEYS) ? 1 : 0);
    // Maj et clic droit font la meme chose : l'un des deux suffit.
    s.sight = this.any(SIGHT_CODES) || this.rightHeld;
  }
}

function clampStep(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < -MAX_MOUSE_STEP ? -MAX_MOUSE_STEP : v > MAX_MOUSE_STEP ? MAX_MOUSE_STEP : v;
}

/** Tous les jetons reconnus, pour un test d'appartenance en O(1). */
const ALL_TOKENS = new Set<string>([
  ...FORWARD_CODES,
  ...BACK_CODES,
  ...LEFT_CODES,
  ...RIGHT_CODES,
  ...TRAVERSE_LEFT,
  ...TRAVERSE_RIGHT,
  ...ELEVATE_UP,
  ...ELEVATE_DOWN,
  ...FIRE_CODES,
  ...SIGHT_CODES,
  ...FORWARD_KEYS,
  ...BACK_KEYS,
  ...LEFT_KEYS,
  ...RIGHT_KEYS,
  ...TRAVERSE_LEFT_KEYS,
  ...TRAVERSE_RIGHT_KEYS,
  ...ELEVATE_UP_KEYS,
  ...ELEVATE_DOWN_KEYS,
]);
